/**
 * OverviewController class handles the initialization and management of the overview page.
 * It interacts with various services to fetch and manipulate task-related data.
 * @class
 * @param {Object} $scope - AngularJS scope object.
 * @param {Object} $rootScope - AngularJS root scope object.
 * @param {Object} $location - AngularJS $location service.
 * @param {Object} $routeParams - AngularJS $routeParams service.
 * @param {Object} $bridges - Service for bridge functionalities.
 * @param {Object} $signals - Service for signal handling.
 * @param {Object} TaskSelectionService - Service for task selection.
 * @param {Object} TaskFlowService - Service for task flow management.
 * @param {Object} TaskDataService - Service for task data management.
 * @param {Object} UserInfoService - Service for user information.
 * @param {Object} TaskCountdownService - Service for task countdown management.
 * @param {Object} $timeout - AngularJS $timeout service.
 * @param {Object} SheetStructureHelperService - Service for sheet structure management.
 * @param {Object} $translate - AngularJS $translate service.
 * @param {Object} VersioningAndLockingService - Service for versioning and locking.
 * @param {Object} TaskInfoService - Service for task information.
 * @param {Object} $config - Configuration service.
 */
export class OverviewController {
    constructor($scope, $rootScope, $location, $routeParams, $bridges, $signals, TaskSelectionService, TaskFlowService, TaskDataService, UserInfoService, TaskCountdownService, $timeout, SheetStructureHelperService, $translate, VersioningAndLockingService, TaskInfoService, $config) {
        this.$scope = $scope;
        this.$rootScope = $rootScope;
        this.$location = $location;
        this.$routeParams = $routeParams;
        this.$bridges = $bridges;
        this.$signals = $signals;
        this.TaskSelectionService = TaskSelectionService;
        this.TaskFlowService = TaskFlowService;
        this.TaskDataService = TaskDataService;
        this.UserInfoService = UserInfoService;
        this.TaskCountdownService = TaskCountdownService;
        this.$timeout = $timeout;
        this.SheetStructureHelperService = SheetStructureHelperService;
        this.$translate = $translate;
        this.VersioningAndLockingService = VersioningAndLockingService;
        this.TaskInfoService = TaskInfoService;
        this.$config = $config;

        // Set the journal flag to false in the root scope
        this.$rootScope.isJournal = false;

        // Fetch task information and set the writing direction and language ISO code
        const taskInfo = this.TaskInfoService.getTaskInfo();
        this.$scope.writingDirection = taskInfo.getInputLanguage().writingDirection;
        this.$scope.langISO = taskInfo.getISOInputLanguageCode();

        // Translate and set the question prefix
        this.$scope._QUESTION_PREFIX = this.$translate.instant("PREFIX.QUESTION");

        // Set CMS flag
        this.$scope.isCMS = window.isCMS;

        // Set availability flags for navigation bar buttons
        this.$scope.navBarBtnPreviewAvailable = true;
        this.$scope.navBarBtnCreateSheetAvailable = true;
        this.$scope.navBarBtnSaveAvailable = true;
        this.$scope.navBarBtnEditIntroductionPageAvailable = true;
        this.$scope.navBarBtnIFrameToolAvailable = true;

        // Handle delete overview image event
        this.$scope.$on('deleteOverviewImage', (event, data) => {
            const images = data.sheet.getPreviewImages();
            data.sheet.setPreviewImages(images.filter(previewImage => previewImage.getUUID() !== data.contentObject.getUUID()));
        });

        // Initialize task flow redirects
        this.TaskFlowService.initRedirects(this.$scope, this.$location);

        // Activate scroll down to continue functionality
        this.activateScrollDownToContinue();

        // Initialize logger and authentication services
        const $logger = this.$bridges.logger();
        const $auth = this.$bridges.authentication();

        // Set log identifier and log initialization entry
        this.$scope._logIdentifier = 'OverviewController';
        $logger.logEnter(this.$scope, 'initialisation');

        // Fetch candidate UUID, user info, task context, and task UUID
        const candidateUUID = $auth.getUserUUID();
        const candidate = this.UserInfoService.getUserInfo(candidateUUID);
        const taskContext = this.TaskDataService.fetchContext();
        const taskUUID = this.TaskSelectionService.getTaskUUID();
        const task = taskContext.read(taskUUID);
        const sheets = task.getSheets();
        const countDown = this.TaskCountdownService.getInstance();

        // Handle journal mode
        if (countDown.getMode() === 'journal') {
            this.$location.path('/sheet/' + task.getJournal().getUUID());
            return false;
        }

        // Set task context overview in window if in CMS mode
        if (window.isCMS) {
            window.taskContextOverview = taskContext;
        }

        // Set sortable options and event handlers if in CMS mode
        if (this.$scope.isCMS) {
            this.$scope.sortableOptions = this.SheetStructureHelperService.getSortableOptions(task);

            this.$scope.onEditSheet = this.onEditSheet.bind(this);
            this.$scope.onDeleteSheet = this.onDeleteSheet.bind(this);
            this.$scope.onSheetUp = this.onSheetUp.bind(this);
            this.$scope.onSheetDown = this.onSheetDown.bind(this);

            // Fetch versioning data and set lock status
            const versioningData = this.VersioningAndLockingService.getData();
            this.$scope.isLocked = this.$rootScope.isPreview ? false : versioningData.locked;

            // Update lock status on versioning data update
            this.$signals.on('versioningDataUpdated', () => {
                const versioningData = this.VersioningAndLockingService.getData();
                this.$scope.isLocked = versioningData.locked;
            });
        }

        // Check task count and root UUID
        const checkTaskCount = Object.keys(taskContext.findInstancesByType('task')).length === 1;
        const checkTaskRootUUID = task.getUUID() === taskUUID;

        // Log warning and fix task root entry point if checks fail
        if (!checkTaskCount || !checkTaskRootUUID) {
            console.warn('Assessment %s has a wrong root access point defined!', taskUUID);
            taskContext.fixTaskRootEntryPoint(taskUUID);
        }

        // Bind isUserAllowed method to scope
        this.$scope.isUserAllowed = this.isUserAllowed.bind(this);

        // Redirect to CMS start page if user is not allowed
        if (window.isCMS && !this.$scope.isUserAllowed()) {
            this.$location.path('/cms/start/');
        }

        // Redirect to sheet if there is only one sheet and not in CMS mode
        if (sheets.length === 1 && !window.isCMS) {
            this.redirectToSheet(sheets[0]);
        }

        // Set last change time and task-related scope variables
        this.$scope.lastChange = (new Date()).getTime();
        this.$scope.sheets = sheets;
        this.$scope.preRelease = task.hasPreRelease() ? task.getPreRelease() : false;
        this.$scope.journal = task.hasJournal() ? task.getJournal() : false;
        this.$scope.task = task;
        this.$scope.candidate = candidate;
        this.$scope.hasPreRelease = task.hasPreRelease();
        this.$scope.hasJournal = task.hasJournal();
        this.$scope.sheetsWithoutPreReleaseAndJournal = task.getSheetsWithoutPreReleaseAndJournal();

        // Redirect to pause page if countdown is paused
        if (countDown.isPaused()) {
            this.$location.path('/pause/' + taskUUID);
            return;
        }

        // Bind sheet panel click and add preview image methods to scope
        this.$scope.onSheetPanelClick = this.onSheetPanelClick.bind(this);
        this.$scope.onAddPreviewImage = this.onAddPreviewImage.bind(this);

        // Handle model update event
        this.$signals.on('modelUpdate', () => {
            console.log('caught an update');
            this.$scope.lastChange = (new Date()).getTime();
            this.$scope.$apply();
        });

        // Clear task-related scope variables on route change start
        this.$scope.$on('$routeChangeStart', (e, next, current) => {
            if (!this.TaskFlowService.isRouteChangeAllowedDuringExam(e, next, current)) {
                console.log('OverviewController routeChangeStart -> disallowed', e, next, current);
                return;
            }
            this.$scope.sheets = [];
            this.$scope.task = {};
            this.$scope.sheetsWithoutPreReleaseAndJournal = {};
            this.$scope.preRelease = null;
            this.$scope.journal = null;
        });

        // Bind checkLanguageAndChangeTitle method to scope
        this.$scope.checkLanguageAndChangeTitle = this.checkLanguageAndChangeTitle.bind(this);

        // Handle reload sheets event
        this.$signals.on("reload_sheets", () => {
            const taskUUID = this.$routeParams.taskUUID;
            const task = taskContext.read(taskUUID);

            this.$scope.sheets = [];
            this.$scope.preRelease = null;
            this.$scope.journal = null;
            this.$scope.hasPreRelease = false;
            this.$scope.hasJournal = false;
            task.initPreReleaseJournal();
            const sheets = task.getSheets();
            const preRelease = task.hasPreRelease() ? task.getPreRelease() : false;
            const journal = task.hasJournal() ? task.getJournal() : false;
            const hasPreRelease = task.hasPreRelease();
            const hasJournal = task.hasJournal();

            this.$scope.hasPreRelease = task.hasPreRelease();
            this.$scope.hasJournal = task.hasJournal();

            this.$timeout(() => {
                this.$scope.$apply(() => {
                    this.$scope.sheets = sheets;
                    this.$scope.preRelease = preRelease;
                    this.$scope.journal = journal;
                    this.$scope.hasPreRelease = hasPreRelease;
                    this.$scope.hasJournal = hasJournal;
                    this.$scope.sheetsWithoutPreReleaseAndJournal = task.getSheetsWithoutPreReleaseAndJournal();
                });
            }, 1);
        });

        // Log initialization exit
        $logger.logExit(this.$scope, 'initialisation');
    }

    /**
     * Redirects to the specified sheet.
     * @param {Object} sheet - The sheet object to redirect to.
     */
    redirectToSheet(sheet) {
        let path;

        if (window.isCMS) {
            path = '/sheet/' + this.$scope.task.getUUID() + '/' + sheet.getUUID();
            if (this.$config.isPreviewMode()) {
                path = '/sheet/' + this.$scope.task.getUUID() + '/' + sheet.getUUID() + "/preview";
            }
        } else {
            path = '/sheet/' + sheet.getUUID();
        }

        this.$location.path(path);
    }

    /**
     * Opens the edit sheet modal.
     * @param {Object} sheet - The sheet object to edit.
     */
    onEditSheet(sheet) {
        this.SheetStructureHelperService.openEditSheetModal('sheetStructureEditor', sheet);
    }

    /**
     * Opens the delete sheet modal.
     * @param {Object} sheet - The sheet object to delete.
     */
    onDeleteSheet(sheet) {
        this.SheetStructureHelperService.openDeleteSheetModal(sheet);
    }

    /**
     * Moves the specified sheet up.
     * @param {Object} sheet - The sheet object to move up.
     * @param {number} index - The current index of the sheet.
     */
    onSheetUp(sheet, index) {
        this.SheetStructureHelperService.sheetUp(this.$scope.task, sheet, index);
    }

    /**
     * Moves the specified sheet down.
     * @param {Object} sheet - The sheet object to move down.
     * @param {number} index - The current index of the sheet.
     */
    onSheetDown(sheet, index) {
        this.SheetStructureHelperService.sheetDown(this.$scope.task, sheet, index);
    }

    /**
     * Checks if the user is allowed to perform certain actions.
     * @returns {boolean} - True if the user is allowed, false otherwise.
     */
    isUserAllowed() {
        return this.UserInfoService.getUserInfo().getPermissions().indexOf('ib_permission_api.can_read_entity') !== -1;
    }

    /**
     * Handles the click event on a sheet panel.
     * @param {Object} sheet - The sheet object that was clicked.
     */
    onSheetPanelClick(sheet) {
        if (!this.$rootScope.isHighlightMode) {
            const $logger = this.$bridges.logger();
            $logger.info(this.$scope, 'clicked for', sheet);
            this.redirectToSheet(sheet);
        }
    }

    /**
     * Adds a preview image to the specified sheet.
     * @param {Object} sheet - The sheet object to add the preview image to.
     */
    onAddPreviewImage(sheet) {
        const uuid = angular.createUUID();
        const contentObjectJSON = {
            "uuid": uuid,
            "content": "",
            "objectType": "image",
            "type": "contentObject",
            "contentReference": false,
            "preferences": {
                "showComments": false
            },
            "entity_content": ""
        };

        const $logger = this.$bridges.logger();
        $logger.info(this.$scope, "Creating image object");
        const contentObject = this.$scope.taskContext.write(uuid, contentObjectJSON);

        let previewImages = sheet.getPreviewImages();
        if (!angular.isArray(previewImages)) {
            $logger.info(this.$scope, "Initialized preview image array for sheet");
            previewImages = [];
        }

        if (previewImages.length >= 3) {
            $logger.info(this.$scope, "Only three overview images allowed.");
            return;
        }

        $logger.info(this.$scope, "Adding image content object to sheet preview images");

        previewImages.push(contentObject.getUUID());

        sheet.setPreviewImages(previewImages);
    }

    /**
     * Checks the language and changes the title accordingly.
     * @param {Object} sheet - The sheet object to check and change the title for.
     * @returns {string} - The modified title.
     */
    checkLanguageAndChangeTitle(sheet) {
        if (this.$scope.langISO === 'sw_SW') {
            const title = sheet.isUseCustomTitle() ? sheet.getTitle() : this.$scope._QUESTION_PREFIX + ' ' + sheet.getShortTitle();
            const totalMarks = sheet.getTotalMarks();
            const translatedMark = this.$translate.instant('CONTENT.MARK', { totalMarks: totalMarks });
            return title + ' ' + ' (' + translatedMark + ' ' + totalMarks + ')';
        }
        return (sheet.isUseCustomTitle() ? sheet.getTitle() : this.$scope._QUESTION_PREFIX + ' ' + sheet.getShortTitle()) + ' (' + sheet.getTotalMarks() + ' ' + this.$translate.instant('CONTENT.MARK', { totalMarks: sheet.getTotalMarks() }) + ') ';
    }

    /**
     * Activates the scroll down to continue functionality.
     */
    activateScrollDownToContinue() {
        // Implementation of the scroll down to continue functionality
    }
}

export default function registerOverviewController(assessmentFrontendControllers) {
    assessmentFrontendControllers.controller('OverviewController', [
        '$scope',
        '$rootScope',
        '$location',
        '$routeParams',
        '$bridges',
        '$signals',
        'TaskSelectionService',
        'TaskFlowService',
        'TaskDataService',
        'UserInfoService',
        'TaskCountdownService',
        '$timeout',
        'SheetStructureHelperService',
        '$translate',
        'VersioningAndLockingService',
        'TaskInfoService',
        '$config',
        OverviewController
    ]);

}